import { CommonModule } from '@angular/common';
import { Component, computed, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { AccountUsersModule, getAccountUsers, tryLoadAccountUsers } from '@capital-access/account/users';
import { CommonFormattingModule } from '@capital-access/common/formatting';
import { CommonLocalizationModule, LocalizationService } from '@capital-access/common/localization';
import {
  ChipSize,
  ChipType,
  FireflyChipModule,
  FireflyDrawerContent,
  FireflyDrawerModule,
  FireflySpinnerModule,
  SpinnerSize
} from '@capital-access/firefly/components';
import { PriorityRating, PriorityRatingLocalized } from '@capital-access/targeting/common';
import { confirmEditTarget, confirmResetRating } from '../../+store/data/actions';
import { CUSTOM_TARGET_LOCALIZATION_SCOPE } from '../../constants';
import { CustomTarget } from '../../models/custom-target.model';

@Component({
  selector: 'ca-customize-target',
  standalone: true,
  imports: [
    CommonModule,
    FireflyDrawerModule,
    FireflyChipModule,
    CommonLocalizationModule,
    AccountUsersModule,
    CommonFormattingModule,
    FireflySpinnerModule
  ],
  templateUrl: './customize-target.component.html',
  styleUrls: ['./customize-target.component.scss']
})
export class CustomizeTargetComponent extends FireflyDrawerContent<{ targetCompany: CustomTarget }> {
  private store = inject(Store);
  targetCompanyData!: CustomTarget;
  selectedInstitutionRating?: PriorityRating;
  initialInstitutionRating?: PriorityRating;

  institutionRatingItems = Object.values(PriorityRating)
    .filter(v => v !== PriorityRating.NR)
    .map(id => ({ id, value: PriorityRatingLocalized[id], disabled: false, iconPopoverContent: '' }));

  accountUsers = this.store.selectSignal(getAccountUsers);
  usersReady = computed(() => this.accountUsers().length > 0);
  localizationScope = CUSTOM_TARGET_LOCALIZATION_SCOPE;
  chipSize = ChipSize.Sm;
  chipType = ChipType.SingleChoice;
  SpinnerSize = SpinnerSize;

  constructor(private readonly localizationService: LocalizationService) {
    super();
    this.store.dispatch(tryLoadAccountUsers());
  }

  override onInjectInputs(input: { targetCompany: CustomTarget }) {
    this.targetCompanyData = input.targetCompany;
    this.initialInstitutionRating =
      this.targetCompanyData.customTargetRatingData?.customRating ??
      this.targetCompanyData.systemRating ??
      PriorityRating.NR;

    this.localizationService
      .localize(
        ['cannotUpdateToSystemRating', 'cannotUpdateToCurrentSelectedRating'],
        {},
        { scope: this.localizationScope }
      )
      .pipe(take(1))
      .subscribe(([selectSystemRatingTooltip, selectCurrentSelectedRatingTooltip]) => {
        this.institutionRatingItems = this.buildInstitutionRatingItems(
          selectSystemRatingTooltip,
          selectCurrentSelectedRatingTooltip
        );
      });
  }

  private buildInstitutionRatingItems(selectSystemRatingTooltip: string, selectCurrentSelectedRatingTooltip: string) {
    return Object.values(PriorityRating)
      .filter(v => v !== PriorityRating.NR)
      .map(id => {
        const isCurrentRating = id === this.initialInstitutionRating;
        const isSystemRating = id === this.targetCompanyData.systemRating;
        const disabled = isCurrentRating || isSystemRating;
        const iconPopoverContent = isCurrentRating
          ? selectCurrentSelectedRatingTooltip
          : isSystemRating
          ? selectSystemRatingTooltip
          : '';

        return {
          id,
          value: PriorityRatingLocalized[id],
          disabled,
          iconPopoverContent
        };
      });
  }

  onSelect(selected: unknown) {
    const item = selected as {
      id: PriorityRating;
      value: PriorityRatingLocalized;
      disabled?: boolean;
      iconPopoverContent?: string;
    };
    // prevent selecting the chip that was active when drawer opened
    if (item.disabled) {
      return;
    }
    this.selectedInstitutionRating = item.id;
  }

  getChipType(item: Record<string, unknown>) {
    return item['id'] === this.selectedInstitutionRating ? ChipType.SingleChoice : ChipType.Enabled;
  }

  get ratingValue() {
    return this.targetCompanyData.systemRating === PriorityRating.NR ? null : this.targetCompanyData.systemRating;
  }

  get localizedPriorityRatingValue() {
    const priorityRating = this.targetCompanyData.systemRating;
    switch (priorityRating) {
      case PriorityRating.HM:
        return 'OV';
      case PriorityRating.HT:
        return 'UN';
      default:
        return priorityRating;
    }
  }

  onSave() {
    this.store.dispatch(
      confirmEditTarget({
        targetCompany: this.targetCompanyData,
        priorityRating: this.selectedInstitutionRating!
      })
    );
  }

  onCancel() {
    this.close();
  }

  onReset() {
    this.store.dispatch(confirmResetRating({ targetCompany: this.targetCompanyData }));
  }
}
